import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { parseISO, format } from "date-fns";
import PrimaryButton from "../buttons/PrimaryButton";
import Skeleton from "react-loading-skeleton";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import "react-loading-skeleton/dist/skeleton.css";
import Cookies from "js-cookie";
import axios from "axios";
import Modal from "../modal/Modal";
import confirmIcon from "../../assets/img/iconamoon--shield-yes-fill.svg";

const SingleSupervisorTable = ({
  siteId,
  activeTab,
  refreshTrigger,
  province,
  district,
  sector,
  onRecordCountsChange,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [dailyRecords, setDailyRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [yellowForms, setYellowForms] = useState([]);
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [formData, setFormData] = useState({
    category: "",
    count: "",
    type: "",
    yellowFormCount: "",
  });
  const [showModal, setShowModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const role = Cookies.get("role");

  // Fetch daily scanned records
  const fetchDailyScannedRecords = async () => {
    try {
      setLoading(true);
      const token = Cookies.get("token");
      const response = await axios.get(
        `${baseUrl}/api/scanned/${siteId}/scanned-records/daily`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newRecords = response.data;
      console.log("new records", newRecords);
      setDailyRecords(newRecords);
      onRecordCountsChange({
        scannedRecords: newRecords.length,
        yellowForms: newRecords.length,
      });
    } catch (error) {
      setError("Error fetching daily records");
    } finally {
      setLoading(false);
    }
  };

  const fetchYellowForms = async () => {
    try {
      setLoading(true);
      const token = Cookies.get("token");
      const response = await axios.get(
        `${baseUrl}/api/yellow-forms/${siteId}/scanned`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setYellowForms(response.data);
      onRecordCountsChange({
        scannedRecords: response.data.length,
        yellowForms: response.data.length,
      });
    } catch (error) {
      setError("Error fetching yellow forms");
    } finally {
      setLoading(false);
    }
  };

  // update scanned record
  const handleEditClick = (record) => {
    setEditingRecordId(record.id);
    if (activeTab === "scanned-records") {
      setFormData({
        category:
          record.book?.name === "Special_category"
            ? "Special Category"
            : record.book?.name || "",
        count: record.count || "",
        type: record.book?.bookType || "",
        yellowFormCount: "",
      });
    } else {
      setFormData({
        category: "",
        count: "",
        type: "",
        yellowFormCount: record.yellowFormCount || "",
      });
    }
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitUpdate = async () => {
    if (!editingRecordId) return;
    activeTab === "scanned-records"
      ? await handleUpdateScannedRecord(editingRecordId)
      : await handleUpdateYellowForm(editingRecordId);
  };
  const handleUpdateScannedRecord = async (recordId) => {
    try {
      setSubmitting(true);
      const token = Cookies.get("token");
      const updateData = {};

      if (formData.count !== "") updateData.count = parseInt(formData.count);

      if (Object.keys(updateData).length === 0) {
        setError("Please provide at least one value to update");
        return;
      }

      const response = await axios.put(
        `${baseUrl}/api/scanned/${siteId}/scanned-records/${recordId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200) {
        setShowModal(false);
        setShowConfirmationModal(true);
        fetchDailyScannedRecords();
        setFormData({ category: "", count: "", type: "", yellowFormCount: "" });
        setError("");
        setEditingRecordId(null);
      }
    } catch (error) {
      setError("Error updating scanned record");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateYellowForm = async (recordId) => {
    try {
      setSubmitting(true);
      const token = Cookies.get("token");
      const updateData = {};

      if (formData.yellowFormCount !== "")
        updateData.yellowFormCount = parseInt(formData.yellowFormCount);

      if (Object.keys(updateData).length === 0) {
        setError("Please provide at least one value to update");
        return;
      }

      const response = await axios.put(
        `${baseUrl}/api/yellow-forms/${siteId}/scanned/${recordId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200) {
        setShowModal(false);
        setShowConfirmationModal(true);
        fetchYellowForms();
        setFormData({ category: "", count: "", type: "", yellowFormCount: "" });
        setError("");
        setEditingRecordId(null);
      }
    } catch (error) {
      setError("Error updating yellow form");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const handleDeleteClick = (recordId) => {
    setRecordToDelete(recordId);
    setShowDeleteConfirm(true);
  };

  // Delete scanned record or yellow form
  const handleDeleteScannedRecord = async () => {
    try {
      setSubmitting(true);
      const token = Cookies.get("token");
      const endpoint =
        activeTab === "scanned-records"
          ? `${baseUrl}/api/scanned/${siteId}/scanned-records/${recordToDelete}`
          : `${baseUrl}/api/yellow-forms/${siteId}/scanned/${recordToDelete}`;
      const response = await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        setShowDeleteConfirm(false);
        setShowDeleteSuccess(true);
        activeTab === "scanned-records"
          ? await fetchDailyScannedRecords()
          : await fetchYellowForms();
        setRecordToDelete(null);
        setFormData({ category: "", count: "", type: "", yellowFormCount: "" });
        setTimeout(() => setShowDeleteSuccess(false), 3000);
      }
    } catch (error) {
      setError("Error deleting scanned record");
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch records based on activeTab
  useEffect(() => {
    if (siteId) {
      if (activeTab === "scanned-records") {
        fetchDailyScannedRecords();
      } else {
        fetchYellowForms();
      }
    }
  }, [siteId, activeTab, refreshTrigger]);

  const formatDate = (dateString) => {
    const parsedDate = parseISO(dateString);
    return format(parsedDate, "dd MMM yyyy");
  };

  const getRecordDetails = () => {
    const records =
      activeTab === "scanned-records" ? dailyRecords : yellowForms;
    const record = records.find((r) => r.id === recordToDelete);
    if (!record) return "this record";
    if (activeTab === "scanned-records") {
      return `${record.count || 0} scanned books for ${sector || district}`;
    } else {
      return `${record.yellowFormCount || 0} yellow forms for ${
        sector || district
      }`;
    }
  };

  const currentRecords =
    activeTab === "scanned-records" ? dailyRecords : yellowForms;

  return (
    <>
      <table>
        <thead>
          <tr>
            {activeTab === "scanned-records" ? (
              <>
                <th>BOOK NUMBER</th>
                <th>BOOK CATEGORY</th>
                <th>BOOK TYPE</th>
                {role === "SCANNER_ASSISTANT" && <th>SCANNER OPERATOR</th>}
                <th>ESTIMATED № OF RECORDS</th>
                <th>DATE</th>
                <th>ACTION</th>
              </>
            ) : (
              <>
                <th>YELLOW FORM COUNT</th>
                {role === "SCANNER_ASSISTANT" && <th>SCANNER OPERATOR</th>}
                <th>DATE</th>
                <th>ACTION</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <tr key={index}>
                {activeTab === "scanned-records" ? (
                  <>
                    <td>
                      <Skeleton width={20} height={20} />
                    </td>
                    <td>
                      <Skeleton width={100} />
                    </td>
                    <td>
                      <Skeleton width={80} />
                    </td>
                    <td>
                      <Skeleton width={100} />
                    </td>
                    <td>
                      <Skeleton width={80} />
                    </td>
                    <td>
                      <Skeleton width={50} />
                    </td>
                  </>
                ) : (
                  <>
                    <td>
                      <Skeleton width={80} />
                    </td>
                    <td>
                      <Skeleton width={80} />
                    </td>
                    <td>
                      <Skeleton width={50} />
                    </td>
                  </>
                )}
              </tr>
            ))
          ) : currentRecords.length === 0 ? (
            <tr style={{ boxShadow: "none" }}>
              <td
                colSpan={activeTab === "scanned-records" ? 7 : 4}
                style={{ textAlign: "center", color: "#9B9B9B" }}
              >
                No records found
              </td>
            </tr>
          ) : (
            currentRecords.map((record) => (
              <tr key={record.id}>
                {activeTab === "scanned-records" ? (
                  <>
                    <td>{record?.bookNumber || "-"}</td>
                    <td>
                      {record.book?.name
                        ? record.book.name === "Special_category"
                          ? "Special Category"
                          : record.book.name
                        : "-"}
                    </td>
                    <td>{record.book?.bookType || "-"}</td>
                    {role === "SCANNER_ASSISTANT" && (
                      <td>{record.assignedToUser?.name || "-"}</td>
                    )}
                    <td>{record.count || 0}</td>
                    <td>{formatDate(record.createdAt)}</td>
                  </>
                ) : (
                  <>
                    <td>{record.yellowFormCount || 0}</td>
                    {role === "SCANNER_ASSISTANT" && (
                      <td>{record.assignedToUser?.name || "-"}</td>
                    )}
                    <td>{formatDate(record.createdAt)}</td>
                  </>
                )}
                <td>
                  <div className="action-buttons">
                    <div className="action-buttons edit-delete">
                      <a
                        onClick={() => handleEditClick(record)}
                        aria-label="Edit"
                        className="icon-button edit-button"
                        disabled={submitting}
                      >
                        <Icon
                          icon="mdi:book-edit"
                          width="20"
                          height="20"
                          color="#FFAF2F"
                        />
                      </a>
                      <a
                        onClick={() => handleDeleteClick(record.id)}
                        aria-label="Delete"
                        className="icon-button delete-button"
                        disabled={submitting}
                      >
                        <Icon
                          icon="mingcute:delete-fill"
                          width="20"
                          height="20"
                          color="#E20000"
                        />
                      </a>
                    </div>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {/* update modal */}

      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        confirm={true}
      >
        <div className="title initialized">UPDATE SCANNED RECORDS</div>
        <div className="content">
          <div className="form-container new-records">
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="scanned-books initialized">
                <div className="address">
                  <div className="address-container">
                    <div className="address-text">Province: {province}</div>
                  </div>
                  <div className="address-container">
                    <div className="address-text">District: {district}</div>
                  </div>
                  {sector && (
                    <div className="address-container">
                      <div className="address-text">Sector: {sector}</div>
                    </div>
                  )}
                </div>
                <div className="data">
                  {activeTab === "scanned-records" ? (
                    <>
                      <div className="book-category-section">
                        <div className="type-heading">Book Category</div>
                        <input
                          className="input-field"
                          placeholder="Book Category"
                          name="category"
                          value={formData.category}
                          onChange={handleInputChange}
                          disabled
                        />
                      </div>

                      <div className="book-category-section">
                        <div className="type-heading">Book Type</div>
                        <input
                          className="input-field"
                          placeholder="Book Type"
                          name="type"
                          value={formData.type}
                          onChange={handleInputChange}
                          disabled
                        />
                      </div>

                      <div className="estimated-records-section">
                        <div className="type-heading">
                          Estimated Number of Records
                        </div>
                        <input
                          className="input-field"
                          placeholder="Count"
                          type="number"
                          name="count"
                          value={formData.count}
                          onChange={handleInputChange}
                          min="1"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="estimated-records-section">
                      <div className="type-heading">Yellow Form Count</div>
                      <input
                        className="input-field"
                        placeholder="Yellow Form Count"
                        type="number"
                        name="yellowFormCount"
                        value={formData.yellowFormCount}
                        onChange={handleInputChange}
                        min="1"
                      />
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="button-group">
            <PrimaryButton
              text={
                submitting ? (
                  <AiOutlineLoading3Quarters className="loader" />
                ) : (
                  "Submit"
                )
              }
              onClick={handleSubmitUpdate}
              disabled={submitting}
            />
            <PrimaryButton
              text="Cancel"
              onClick={() => setShowModal(false)}
              disabled={submitting}
            />
          </div>
        </div>
      </Modal>
      <Modal
        show={showConfirmationModal}
        onClose={handleCloseConfirmationModal}
      >
        <div className="content confirmation">
          <div className="icon-container">
            <img
              src={confirmIcon}
              alt="Confirm"
              width="57"
              height="57"
              style={{ color: "#078ECE" }}
            />
          </div>
          <div className="text">Record has been updated successfully!</div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <div className="content confirmation">
          <h2>Confirm Delete</h2>
          <div className="text">
            Are you sure you want to delete {getRecordDetails()}?
          </div>
          <div className="button-group">
            <PrimaryButton
              text={
                submitting ? (
                  <AiOutlineLoading3Quarters className="loader" />
                ) : (
                  "Yes"
                )
              }
              onClick={handleDeleteScannedRecord}
              disabled={submitting}
            />
            <PrimaryButton
              text="No"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={submitting}
            />
          </div>
        </div>
      </Modal>

      {/* Delete Success Modal */}
      <Modal
        show={showDeleteSuccess}
        onClose={() => setShowDeleteSuccess(false)}
      >
        <div className="content confirmation">
          <div className="icon-container">
            <img src={confirmIcon} alt="confirm" width="57" height="57" />
          </div>
          <div className="text">Record has been successfully deleted!</div>
        </div>
      </Modal>

      {error && <div className="error-message">{error}</div>}
    </>
  );
};

export default SingleSupervisorTable;
